import {
  anniversariesFromBirthdays,
  isBirthdayDerivedId,
  isMilestoneAnniversary,
  sortAnniversariesByCountdown,
  type Anniversary,
  type AnniversaryIcon,
  type AnniversaryKind,
} from "@couples/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Heart, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import MilestoneRibbon from "../components/MilestoneRibbon";
import OverlayShell from "../components/OverlayShell";
import QueryState from "../components/QueryState";
import { errorMessageOf } from "../lib/auth";
import { fetchBirthdays } from "../lib/birthdays";
import { useConfirm } from "../lib/confirm";
import { queryKeys } from "../lib/queryKeys";
import {
  ANNIVERSARY_ICON_OPTIONS,
  anniversaryIconComponentOf,
} from "../lib/schedule/anniversaryIcons";
import {
  createAnniversary,
  deleteAnniversary,
  fetchAnniversaries,
  updateAnniversary,
  type AnniversaryInput,
} from "../lib/schedule/anniversaries";
import { useToast } from "../lib/toast";

interface Draft {
  id: string | null;
  title: string;
  baseDate: string;
  kind: AnniversaryKind;
  icon: AnniversaryIcon;
  memo: string;
}

function todayKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export default function AnniversaryManager({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  const anniversaries = useQuery({
    queryKey: queryKeys.anniversaries,
    queryFn: fetchAnniversaries,
  });
  const birthdays = useQuery({ queryKey: queryKeys.birthdays, queryFn: fetchBirthdays });

  // 誕生日は Settings のプロフィール値から毎回作る。ここでは編集・削除できない
  // (isBirthdayDerivedId で判定し、タップしても何もしない)。
  const allAnniversaries = [
    ...(anniversaries.data ?? []),
    ...anniversariesFromBirthdays(birthdays.data ?? []),
  ];
  const sorted = sortAnniversariesByCountdown(allAnniversaries, new Date());

  function startAdd() {
    setDraft({
      id: null,
      title: "",
      baseDate: todayKey(),
      kind: "anniversary",
      icon: "heart",
      memo: "",
    });
  }

  function startEdit(anniversary: Anniversary) {
    setDraft({
      id: anniversary.id,
      title: anniversary.title,
      baseDate: anniversary.baseDate,
      kind: anniversary.kind,
      icon: anniversary.icon,
      memo: anniversary.memo ?? "",
    });
  }

  async function save() {
    if (draft === null) return;
    const title = draft.title.trim();
    if (title === "") return;
    const memo = draft.memo.trim();
    const input: AnniversaryInput = {
      title,
      baseDate: draft.baseDate,
      kind: draft.kind,
      icon: draft.icon,
      memo: memo === "" ? null : memo,
    };

    setBusy(true);
    try {
      if (draft.id !== null) {
        await updateAnniversary(draft.id, input);
      } else {
        await createAnniversary(input);
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.anniversaries });
      setDraft(null);
      toast(draft.id !== null ? "記念日を変更しました。" : "記念日を追加しました。");
    } catch (error) {
      toast(errorMessageOf(error), "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (draft?.id === null || draft === null) return;
    const ok = await confirm({
      message: `「${draft.title}」を消します。`,
      confirmLabel: "記念日を消す",
      tone: "danger",
    });
    if (!ok) return;

    setBusy(true);
    try {
      await deleteAnniversary(draft.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.anniversaries });
      setDraft(null);
      toast("記念日を消しました。");
    } catch (error) {
      toast(errorMessageOf(error), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <OverlayShell
      title="記念日"
      icon={Heart}
      onClose={onClose}
      width="grid"
      actions={
        <button type="button" onClick={startAdd} aria-label="記念日を追加">
          <Plus className="size-5 text-[var(--color-sub)]" aria-hidden />
        </button>
      }
    >
      <div className="flex flex-col gap-2.5 p-4">
        {draft !== null && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
            className="flex flex-col gap-3 rounded-2xl border border-[var(--color-line)] bg-white p-3.5"
          >
            <label className="block">
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">タイトル</span>
              <input
                autoFocus
                value={draft.title}
                onChange={(event) => {
                  setDraft({ ...draft, title: event.target.value });
                }}
                placeholder="例: 結婚記念日"
                className="w-full rounded-lg border border-[var(--color-line)] px-2.5 py-2 text-base outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">起算日</span>
              <input
                type="date"
                value={draft.baseDate}
                onChange={(event) => {
                  setDraft({ ...draft, baseDate: event.target.value });
                }}
                className="w-full rounded-lg border border-[var(--color-line)] px-2.5 py-2 text-base outline-none"
              />
            </label>
            <div>
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">アイコン</span>
              <div className="flex flex-wrap gap-1.5">
                {ANNIVERSARY_ICON_OPTIONS.map(({ value, label, Icon }) => {
                  const selected = draft.icon === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-label={label}
                      aria-pressed={selected}
                      onClick={() => {
                        setDraft({ ...draft, icon: value });
                      }}
                      className={`flex size-10 items-center justify-center rounded-full border-[1.5px] ${
                        selected
                          ? "border-[var(--color-gold)] bg-[var(--color-gold)]/15 text-[var(--color-plum)]"
                          : "border-[var(--color-line)] bg-white text-[var(--color-sub)]"
                      }`}
                    >
                      <Icon className="size-4.5" aria-hidden />
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="block">
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">メモ</span>
              <textarea
                value={draft.memo}
                onChange={(event) => {
                  setDraft({ ...draft, memo: event.target.value });
                }}
                placeholder="例: 毎年ここでケーキを買う"
                rows={2}
                className="w-full resize-none rounded-lg border border-[var(--color-line)] px-2.5 py-2 outline-none"
              />
            </label>
            <div className="flex gap-2.5">
              {draft.id !== null && (
                <button
                  type="button"
                  onClick={() => void remove()}
                  disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-[var(--color-line)] px-3.5 text-[12.5px] text-[var(--color-plum)] disabled:opacity-50"
                >
                  <Trash2 className="size-3.5" aria-hidden />
                  削除
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setDraft(null);
                }}
                className="rounded-lg px-3.5 text-[12.5px] text-[var(--color-sub)]"
              >
                やめる
              </button>
              <button
                type="submit"
                disabled={busy || draft.title.trim() === ""}
                className="flex-1 rounded-xl bg-[var(--color-teal)] py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                保存
              </button>
            </div>
          </form>
        )}

        <QueryState
          isPending={anniversaries.isPending || birthdays.isPending}
          isError={anniversaries.isError || birthdays.isError}
          errorMessage="記念日を取得できませんでした。"
          isEmpty={
            anniversaries.isSuccess && birthdays.isSuccess && sorted.length === 0 && draft === null
          }
          emptyMessage="まだ記念日がありません。"
          emptyClassName="p-10 text-center text-sm"
        />

        <div className="grid grid-cols-2 gap-2.5 [grid-auto-flow:dense] lg:grid-cols-3">
          {sorted.map(({ anniversary, occurrence, days }) => {
            const suffix =
              anniversary.kind === "birthday" ? `${occurrence.count}歳` : `${occurrence.count}周年`;
            const [by, bm, bd] = anniversary.baseDate.split("-");
            const isFromProfile = isBirthdayDerivedId(anniversary.id);
            const Icon = anniversaryIconComponentOf(anniversary.icon);
            const milestone = isMilestoneAnniversary(anniversary.kind, occurrence.count);
            return (
              <button
                key={anniversary.id}
                type="button"
                onClick={() => {
                  if (!isFromProfile) startEdit(anniversary);
                }}
                disabled={isFromProfile}
                className={`relative flex flex-col gap-1.5 overflow-hidden rounded-2xl bg-gradient-to-br from-white to-[var(--color-gold)]/[0.14] text-left shadow-sm ring-1 ring-[var(--color-gold)]/25 disabled:cursor-default ${
                  milestone ? "col-span-2 p-4 ring-[1.5px] ring-[var(--color-gold)]/60" : "p-3.5"
                }`}
              >
                <span
                  className="pointer-events-none absolute -top-5 -right-5 size-20 rounded-full bg-[var(--color-gold)]/10"
                  aria-hidden
                />
                {milestone && <MilestoneRibbon label={suffix} />}
                <div className="relative flex items-center justify-between gap-1">
                  <span
                    className={`flex shrink-0 items-center justify-center rounded-full bg-[var(--color-gold)]/15 ${
                      milestone ? "size-11" : "size-9"
                    }`}
                  >
                    <Icon
                      className={`text-[var(--color-plum)] ${milestone ? "size-5" : "size-4"}`}
                      aria-hidden
                    />
                  </span>
                  {isFromProfile ? (
                    <span className="shrink-0 text-[10px] text-[var(--color-sub)]">設定で編集</span>
                  ) : (
                    <span className="flex shrink-0 items-baseline gap-0.5">
                      <span
                        className={`leading-none font-bold [font-variant-numeric:tabular-nums] text-[var(--color-plum)] ${
                          milestone ? "text-[30px]" : "text-[20px]"
                        }`}
                      >
                        {days}
                      </span>
                      <span className="text-[10px] text-[var(--color-sub)]">日</span>
                    </span>
                  )}
                </div>
                <div className="relative min-w-0">
                  <div
                    className={`truncate font-bold ${milestone ? "text-[15.5px]" : "text-[13.5px]"}`}
                  >
                    {anniversary.title}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-[var(--color-sub)]">
                    {by}年{Number(bm)}月{Number(bd)}日〜 · {suffix}
                  </div>
                  {anniversary.memo !== null && anniversary.memo !== "" && (
                    <div className="mt-1 line-clamp-2 text-[11px] text-[var(--color-sub)]">
                      {anniversary.memo}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </OverlayShell>
  );
}
